// app/frontend/components/ui/ImageLightbox.tsx

import { useState } from 'react'
import { X, Download, ExternalLink } from 'lucide-react'
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
} from '@/components/ui/Dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'

interface ImageLightboxProps {
  src: string
  thumbnailSrc?: string
  alt: string
  className?: string
}

export function ImageLightbox({ src, thumbnailSrc, alt, className }: ImageLightboxProps) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            "block max-w-[300px] rounded-lg overflow-hidden mt-2 hover:opacity-90 transition-opacity cursor-pointer",
            className
          )}
        >
          <img
            src={thumbnailSrc || src}
            alt={alt}
            className="w-full h-auto"
            loading="lazy"
          />
        </button>
      </DialogPrimitive.Trigger>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-4 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onClick={() => setOpen(false)}
        >
          {/* Hidden title for accessibility */}
          <DialogPrimitive.Title className="sr-only">
            {alt}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Image preview
          </DialogPrimitive.Description>

          {/* Toolbar */}
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <a
              href={src}
              download
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              title="Download"
            >
              <Download className="size-5" />
            </a>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="size-5" />
            </a>
            <DialogClose className="p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors">
              <X className="size-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>

          {/* Image */}
          <img
            src={src}
            alt={alt}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

export default ImageLightbox
